import ProfilePictureUpload from "@/components/ui/ProfilePictureUpload.jsx";
import AvatarHeaderPositioned from "@/sot/primitives/AvatarHeaderPositioned.jsx";

const DEFAULT_AVATAR = "/icons/Profile_Avatar_borderless.svg";
const EDIT_AVATAR = "/icons/clinicians/edit_ped_full.svg";

/**
 * The single avatar contract for Institution editors.
 *
 * Consumers that provide `onCroppedFile` keep the cropped image in local
 * component state; ProfilePictureUpload only persists when that callback is
 * intentionally omitted.
 */
export default function InstitutionProfileAvatar({
  ariaLabel,
  changeLabel,
  currentImageUrl = "",
  displayName = "",
  disabled = false,
  readOnly = false,
  onCroppedFile,
  profileHeader = false,
  railClassName,
  mobileOffsetClassName,
}) {
  const avatarSource = currentImageUrl || DEFAULT_AVATAR;

  if (profileHeader) {
    return <AvatarHeaderPositioned
      previewUrl={currentImageUrl}
      alt={ariaLabel}
      interactive
      showEditChip={false}
      railClassName={railClassName}
      mobileOffsetClassName={mobileOffsetClassName}
      placeholderSrc={DEFAULT_AVATAR}
    >
      <ProfilePictureUpload
        compactTrigger
        accountLayout
        currentImageUrl={currentImageUrl}
        displayName={displayName}
        disabled={disabled}
        onCroppedFile={onCroppedFile}
        renderPreview={({ src, onSelect }) => (
          <button
            type="button"
            className="afrimed-profile-avatar h-20 w-20 md:h-[150px] md:w-[150px]"
            onClick={disabled ? undefined : onSelect}
            disabled={disabled}
            aria-label={changeLabel}
          >
            <img src={src || DEFAULT_AVATAR} alt="" className={src ? "object-cover" : "object-contain"} />
            {src && !disabled ? <span className="absolute -bottom-1 -right-1 z-10 h-7 w-7" aria-hidden="true"><img src={EDIT_AVATAR} alt="" className="h-full w-full" /></span> : null}
          </button>
        )}
      />
    </AvatarHeaderPositioned>;
  }

  if (readOnly) {
    return <section className="institution-clinician-avatar-block" aria-label={ariaLabel}>
      <span className={`institution-clinician-avatar-surface institution-clinician-avatar-surface--readonly${currentImageUrl ? " is-uploaded" : " is-fallback"}`}>
        <img src={avatarSource} alt="" />
      </span>
    </section>;
  }

  return (
    <section className="institution-clinician-avatar-block" aria-label={ariaLabel}>
      <ProfilePictureUpload
        compactTrigger
        accountLayout
        currentImageUrl={currentImageUrl}
        displayName={displayName}
        disabled={disabled}
        onCroppedFile={onCroppedFile}
        renderPreview={({ src, onSelect }) => (
          <button
            type="button"
            className={`institution-clinician-avatar-surface${src ? " is-uploaded" : " is-fallback"}`}
            onClick={disabled ? undefined : onSelect}
            disabled={disabled}
            aria-label={changeLabel}
          >
            <img src={src || DEFAULT_AVATAR} alt="" />
            {src && !disabled ? (
              <span className="institution-clinician-avatar-edit" aria-hidden="true">
                <img src={EDIT_AVATAR} alt="" />
              </span>
            ) : null}
          </button>
        )}
      />
    </section>
  );
}

export { DEFAULT_AVATAR, EDIT_AVATAR };
